import { useEffect, useState, useCallback } from 'react';
import {
    Tenant,
    fetchTenant as apiFetchTenant,
    updateTenant as apiUpdateTenant,
} from '../api/TenantApi';
import { ApiError } from '../api/types';

export type UpdateTenantData = Partial<Omit<Tenant, 'id'>>;

interface UseTenantReturn {
    tenant: Tenant | undefined;
    loading: boolean;
    error: string | null;

    // Action states
    updating: boolean;

    // Actions
    update: (tenantData: UpdateTenantData) => Promise<Tenant | null>;
    refresh: () => Promise<void>;
}

export function useTenant(): UseTenantReturn {

    const [tenant, setTenant] = useState<Tenant>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        loadTenant();
    }, []);

    const loadTenant = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetchTenant();
            setTenant(response.data);
        } catch (error) {
            console.error('Error loading tenant:', error);
            setError('Failed to load tenant information');
        } finally {
            setLoading(false);
        }
    };

    const refresh = useCallback(async () => {
        await loadTenant();
    }, []);

    const update = useCallback(async (tenantData: UpdateTenantData): Promise<Tenant | null> => {
        setUpdating(true);
        try {
            const response = await apiUpdateTenant(tenantData);

            if (response.success && response.data) {
                setTenant(response.data);
                return response.data;
            } else {
                throw new ApiError(response.error, 'Failed to update tenant');
            }
        } catch (err) {
            console.error('Error updating tenant:', err);
            throw err;
        } finally {
            setUpdating(false);
        }
    }, []);

    return {
        tenant,
        loading,
        error,
        updating,
        update,
        refresh,
    };
}

export default useTenant;